import type { MessengerPlatform } from '../bridge'
import {
  devOrganizationId,
  devRfqId,
  devSecret,
  isDevMode,
} from '../config/devMode'
import { ApiError, apiRequest, setAccessToken } from './client'
import type { AuthSession, MiniAppAuthResponse } from './types'

const SAME_ORIGIN_API_PREFIX = '/api/v1'

const authPathFor = (platform: MessengerPlatform): string => {
  if (platform === 'max') {
    return '/integrations/max/miniapp/auth'
  }
  return '/integrations/telegram/miniapp/auth'
}

const readErrorMessage = async (response: Response): Promise<string> => {
  const fallback = response.statusText || 'Request failed'
  try {
    const payload = await response.json() as { message?: string; error?: string }
    return payload.message || payload.error || fallback
  } catch {
    return fallback
  }
}

const postDevAuthSameOrigin = async (params: {
  initData: string
  startParam: string | null
  organizationId: number | null
  rfqId: number | null
  secret: string
}): Promise<MiniAppAuthResponse> => {
  const response = await fetch(`${SAME_ORIGIN_API_PREFIX}/integrations/telegram/miniapp/dev-auth`, {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      'X-Miniapp-Dev-Secret': params.secret,
    },
    body: JSON.stringify({
      initData: params.initData || undefined,
      start_param: params.startParam ?? undefined,
      organization_id: params.organizationId ?? undefined,
      rfq_id: params.rfqId ?? undefined,
    }),
  })

  if (!response.ok) {
    const message = await readErrorMessage(response)
    throw new ApiError(response.status, message)
  }

  return await response.json() as MiniAppAuthResponse
}

export type AuthResult =
  | { kind: 'linked'; session: AuthSession }
  | { kind: 'not_linked'; rfqId: number | null; linkBotUsername: string; message: string }
  | { kind: 'unsupported'; platform: MessengerPlatform }
  | { kind: 'error'; message: string }

const sessionFromResponse = (response: MiniAppAuthResponse): AuthSession => ({
  accessToken: response.access_token!,
  expiresIn: response.expires_in ?? 3600,
  organizationId: response.organization_id ?? 0,
  rfqId: response.rfq_id,
})

export type DevAuthOptions = {
  organizationId?: number | null
  rfqId?: number | null
}

export const authenticateDev = async (
  initData: string,
  startParam: string | null,
  options: DevAuthOptions = {},
): Promise<AuthResult> => {
  const secret = devSecret()
  if (!secret) {
    return {
      kind: 'error',
      message:
        'Dev mode: задайте VITE_MINIAPP_DEV_SECRET (и на backend PROVISIUM_MINIAPP_DEV_MODE=true).',
    }
  }

  const organizationId = options.organizationId ?? devOrganizationId()
  const rfqId = options.rfqId ?? devRfqId()

  try {
    const response = await postDevAuthSameOrigin({
      initData,
      startParam,
      organizationId,
      rfqId,
      secret,
    })

    if (!response.linked || !response.access_token) {
      return {
        kind: 'error',
        message: response.message ?? 'Dev auth не выдал токен',
      }
    }

    const session = sessionFromResponse(response)
    setAccessToken(session.accessToken)
    return { kind: 'linked', session }
  } catch (error) {
    if (error instanceof TypeError) {
      return {
        kind: 'error',
        message:
          'Нет связи с API. Запустите npm run dev и проверьте VITE_API_BASE_URL=/api/v1 (прокси Vite).',
      }
    }
    if (error instanceof ApiError) {
      return {
        kind: 'error',
        message:
          error.status >= 500
            ? 'Dev-auth на API недоступен (ошибка сервера). Нужен корректный VITE_MINIAPP_DEV_SECRET с VPS или локальный backend.'
            : `API ${error.status}: ${error.message}`,
      }
    }
    const message = error instanceof Error ? error.message : 'Dev auth failed'
    return { kind: 'error', message }
  }
}

export const authenticate = async (
  platform: MessengerPlatform,
  initData: string,
  startParam: string | null,
): Promise<AuthResult> => {
  if (isDevMode) {
    const devResult = await authenticateDev(initData, startParam)
    if (devResult) {
      return devResult
    }
  }

  if (platform === 'browser') {
    return {
      kind: 'unsupported',
      platform,
    }
  }

  if (platform === 'max') {
    return {
      kind: 'error',
      message: 'Авторизация MAX пока не подключена на backend. Используйте Telegram.',
    }
  }

  if (!initData) {
    return {
      kind: 'error',
      message: 'Нет initData от мессенджера. Откройте приложение из бота.',
    }
  }

  try {
    const response = await apiRequest<MiniAppAuthResponse>(authPathFor(platform), {
      body: {
        initData,
        start_param: startParam ?? undefined,
      },
      auth: false,
    })

    if (!response.linked || !response.access_token) {
      if (isDevMode) {
        const devFallback = await authenticateDev(initData, startParam)
        if (devFallback) {
          return devFallback
        }
      }

      return {
        kind: 'not_linked',
        rfqId: response.rfq_id,
        linkBotUsername: response.link_bot_username ?? 'provisium_market_bot',
        message:
          response.message ??
          'Подключите Telegram в личном кабинете Provisium (уведомления → market bot).',
      }
    }

    const session = sessionFromResponse(response)
    setAccessToken(session.accessToken)
    return { kind: 'linked', session }
  } catch (error) {
    if (isDevMode) {
      const devFallback = await authenticateDev(initData, startParam)
      if (devFallback?.kind === 'linked') {
        return devFallback
      }
    }

    const message = error instanceof Error ? error.message : 'Ошибка авторизации'
    return { kind: 'error', message }
  }
}
